import React, { useMemo, useState } from "react";
import { Box, Typography, IconButton, Tooltip, Button, CircularProgress } from "@mui/material";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useStudioTheme } from "../constants";
import { cleanImportedMarkdown } from "../generateDocument";

const buildMarkdownComponents = ({ palette, fontStack, serifStack }) => ({
  h1: ({ node, ...props }) => (
    <Typography
      component="h1"
      sx={{
        fontFamily: serifStack,
        fontSize: "26px",
        fontWeight: 700,
        color: palette.ink,
        mt: 0,
        mb: 1.5,
        letterSpacing: "-0.3px",
      }}
      {...props}
    />
  ),
  h2: ({ node, ...props }) => (
    <Typography
      component="h2"
      sx={{
        fontFamily: fontStack,
        fontSize: "17px",
        fontWeight: 700,
        color: palette.ink,
        mt: 3,
        mb: 1,
        pb: 0.5,
        borderBottom: `1px solid ${palette.border}`,
      }}
      {...props}
    />
  ),
  h3: ({ node, ...props }) => (
    <Typography
      component="h3"
      sx={{
        fontFamily: fontStack,
        fontSize: "15px",
        fontWeight: 700,
        color: palette.inkSoft,
        mt: 2,
        mb: 0.75,
      }}
      {...props}
    />
  ),
  p: ({ node, ...props }) => (
    <Typography
      component="p"
      sx={{
        fontFamily: fontStack,
        fontSize: "14.5px",
        lineHeight: 1.7,
        color: palette.inkSoft,
        mb: 1.25,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
      {...props}
    />
  ),
  ul: ({ node, ...props }) => (
    <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5, fontFamily: fontStack }} {...props} />
  ),
  ol: ({ node, ...props }) => (
    <Box component="ol" sx={{ m: 0, mb: 1.5, pl: 2.5, fontFamily: fontStack }} {...props} />
  ),
  li: ({ node, ordered, ...props }) => (
    <Box
      component="li"
      sx={{
        fontFamily: fontStack,
        fontSize: "14.5px",
        lineHeight: 1.7,
        color: palette.inkSoft,
        mb: 0.5,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
      {...props}
    />
  ),
  strong: ({ node, ...props }) => (
    <Box
      component="strong"
      sx={{ fontWeight: 700, color: palette.ink, overflowWrap: "anywhere" }}
      {...props}
    />
  ),
  blockquote: ({ node, ...props }) => (
    <Box
      component="blockquote"
      sx={{
        m: 0,
        my: 1.25,
        pl: 1.5,
        borderLeft: `2px solid ${palette.borderStrong}`,
        color: palette.inkSoft,
        fontFamily: fontStack,
        fontSize: "14.5px",
        lineHeight: 1.65,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        "& p": { m: 0, mb: 0.75, "&:last-child": { mb: 0 } },
      }}
      {...props}
    />
  ),
  table: ({ node, ...props }) => (
    <Box sx={{ overflowX: "auto", my: 1.5 }}>
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: fontStack,
          fontSize: "13.5px",
          "& th, & td": {
            border: `1px solid ${palette.border}`,
            px: 1.25,
            py: 0.9,
            textAlign: "left",
          },
          "& th": {
            backgroundColor: palette.canvasAlt,
            color: palette.ink,
            fontWeight: 700,
          },
          "& td": { color: palette.inkSoft },
        }}
        {...props}
      />
    </Box>
  ),
  hr: () => <Box sx={{ my: 2.5, borderTop: `1px solid ${palette.border}` }} />,
  a: ({ node, ...props }) => (
    <Box component="a" sx={{ color: palette.brand, textDecoration: "none" }} {...props} />
  ),
});

const EmptyPreview = () => {
  const { palette, shadow, fontStack } = useStudioTheme();

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 4,
        color: palette.inkFaint,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "18px",
          backgroundColor: palette.paper,
          border: `1px solid ${palette.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          boxShadow: shadow.sm,
        }}
      >
        <DescriptionRoundedIcon sx={{ fontSize: 30, color: palette.brandSoft }} />
      </Box>
      <Typography
        sx={{
          fontFamily: fontStack,
          fontSize: "15px",
          fontWeight: 600,
          color: palette.inkSoft,
          mb: 0.5,
        }}
      >
        Your document will appear here
      </Typography>
      <Typography sx={{ fontFamily: fontStack, fontSize: "13.5px", maxWidth: 280 }}>
        Send a request on the left and Rego AI will draft it live in this panel.
      </Typography>
    </Box>
  );
};

const LoadingPreview = () => {
  const { palette, fontStack } = useStudioTheme();

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 4,
        color: palette.inkFaint,
      }}
    >
      <CircularProgress size={30} sx={{ color: palette.brandSoft, mb: 2 }} />
      <Typography
        sx={{
          fontFamily: fontStack,
          fontSize: "15px",
          fontWeight: 600,
          color: palette.inkSoft,
          mb: 0.5,
        }}
      >
        Loading the document…
      </Typography>
      <Typography sx={{ fontFamily: fontStack, fontSize: "13.5px", maxWidth: 320 }}>
        Fetching the current version and preparing it for editing with Rego AI.
      </Typography>
    </Box>
  );
};

const DocumentPreview = ({ title, document, streaming, loadingDoc }) => {
  const [copied, setCopied] = useState(false);
  const { palette, shadow, fontStack } = useStudioTheme();
  const markdownComponents = useMemo(
    () => buildMarkdownComponents({ palette, fontStack, serifStack: fontStack }),
    [palette, fontStack]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(document || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  const handleDownload = () => {
    const blob = new Blob([document || ""], {
      type: "text/markdown;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = `${(title || "document").replace(/\s+/g, "-").toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: palette.canvas,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.75,
          borderBottom: `1px solid ${palette.border}`,
          backgroundColor: palette.paper,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <DescriptionRoundedIcon sx={{ fontSize: 20, color: palette.brand, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontFamily: fontStack,
                fontSize: "14px",
                fontWeight: 600,
                color: palette.ink,
                letterSpacing: "-0.01em",
              }}
            >
              {title || "Untitled document"}
            </Typography>
            <Typography
              sx={{
                fontFamily: fontStack,
                fontSize: "12px",
                color: palette.inkFaint,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {loadingDoc ? (
                <>
                  <TipsAndUpdatesRoundedIcon sx={{ fontSize: 12 }} /> Loading…
                </>
              ) : streaming ? (
                <>
                  <TipsAndUpdatesRoundedIcon sx={{ fontSize: 12 }} /> Updating…
                </>
              ) : (
                "Live preview"
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Tooltip title={copied ? "Copied" : "Copy markdown"}>
            <span>
              <IconButton
                size="small"
                onClick={handleCopy}
                disabled={!document}
                sx={{
                  color: copied ? palette.success : palette.inkSoft,
                  border: `1px solid ${palette.border}`,
                  borderRadius: "8px",
                  width: 34,
                  height: 34,
                }}
              >
                {copied ? (
                  <CheckRoundedIcon fontSize="small" />
                ) : (
                  <ContentCopyRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handleDownload}
            disabled={!document}
            startIcon={<DownloadRoundedIcon />}
            sx={{
              fontFamily: fontStack,
              fontSize: "13px",
              fontWeight: 600,
              textTransform: "none",
              px: 1.75,
              py: 0.75,
              borderRadius: "8px",
              boxShadow: "none",
              bgcolor: document ? palette.buttonPrimary : palette.borderStrong,
              "&:hover": {
                bgcolor: document ? palette.buttonHover : palette.borderStrong,
                boxShadow: "none",
              },
            }}
          >
            Save policy
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: 0,
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          px: { xs: 2, md: 4 },
          py: 3,
        }}
      >
        {loadingDoc && !document ? (
          <LoadingPreview />
        ) : document ? (
          <Box
            sx={{
              width: "100%",
              maxWidth: 680,
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              backgroundColor: palette.paper,
              borderRadius: "8px",
              boxShadow: shadow.document,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                height: 0,
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                px: { xs: 2.5, md: 4 },
                py: { xs: 2.5, md: 4 },
                minWidth: 0,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                "& pre": {
                  maxWidth: "100%",
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                },
                "& img": { maxWidth: "100%", height: "auto" },
                "& table": { maxWidth: "100%" },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {cleanImportedMarkdown(document)}
              </ReactMarkdown>
            </Box>
          </Box>
        ) : (
          <EmptyPreview />
        )}
      </Box>
    </Box>
  );
};

export default DocumentPreview;
